import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateAutomationTemplateDraftStateQuery } from '../../queries/useAutomationTemplateHasDraftQuery';
import { AUTOMATION_QUERY_KEYS, AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';

interface Params {
	templateId: string;
	stepType: Api.AutomationStepType;
	stepId: string;
	step: Api.IAutomationStep;
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * Additional template queries that should be invalidated after this save.
	 * Used by the canvas editor when a nested branch step update should also
	 * refresh the root template's draft/published state.
	 */
	relatedTemplateIds?: string[];
}

export function useUpdateAutomationStepMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationStep, variables: Params) => void;
	onError?: (error: Api.IOperationResultNoValue, variables: Params) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({ templateId, stepType, stepId, step, impersonationContext }: Params) => {
			return userSession.webServiceHelper.callAsync<Api.IAutomationStep>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(templateId)}/Step/${encodeURIComponent(
						stepType
					)}/${encodeURIComponent(stepId)}`,
					impersonationContext,
				}),
				'PUT',
				step
			);
		},
		onError,
		onSuccess: (data, variables) => {
			const templateIdsToInvalidate = new Set([variables.templateId, ...(variables.relatedTemplateIds ?? [])]);
			for (const templateId of templateIdsToInvalidate) {
				queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(templateId) });
				invalidateAutomationTemplateDraftStateQuery(templateId);
			}
			onSuccess?.(data, variables);
		},
	});
}
